import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
  BenchmarkStandardsOverTimeReport
  // eslint-disable-next-line max-len
} from '@shared/branded-reports/branded-benchmark-reports/admin-standards-over-time-report/standards-over-time-report-detail.model';
import {
  BarColors,
  BorderColors,
  BorderStyles,
  TextColors
} from '@shared/branded-reports/zbchart-report/bar-colors.enum';
import { ZbChartReportTableComponent } from '@shared/branded-reports/zbchart-report/zbchart-report-table.component';
import { ZbChartBar, ZbChartBarValue, ZbChartData } from '@shared/branded-reports/zbchart-report/zbchart-report.model';
import { BehaviorSubject, Subscription } from 'rxjs';

@Component({
  selector: 'zbp-standards-over-time-report-detail',
  imports: [
    CommonModule,
    ZbChartReportTableComponent
  ],
  templateUrl: './standards-over-time-report-detail.component.html',
})
export class StandardsOverTimeReportDetailComponent implements OnInit, OnChanges, OnDestroy {
  @Input() standardsOverTimeReport?: BenchmarkStandardsOverTimeReport;

  zbChart: ZbChartData;
  private reports$ = new BehaviorSubject<BenchmarkStandardsOverTimeReport>(null);
  private readonly subscriptions: Subscription = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(
      this.reports$.subscribe((report) => {
        this.createZbChart(report);
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.standardsOverTimeReport) {
      this.reports$.next(changes.standardsOverTimeReport.currentValue);
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  createZbChart(
    report: BenchmarkStandardsOverTimeReport,
  ): ZbChartData {
    if (!report) {
      return null;
    }

    const chartData = { bars: [] } as ZbChartData;
    chartData.showZeroCount = true;

    report.benchmarkResults.forEach((result) => {
      let ariaLabel: string;
      const row = {} as ZbChartBar;
      const values = [] as ZbChartBarValue[];

      row.label = result.assessmentName;
      row.labelOnLeft = false;

      if (result.assessmentPercentageChange) {
        row.totalCount = 100;
        ariaLabel = `Average score for this assessment is ${result.assessmentPercentageChangeFormatted}`;
        values.push({
          barLabel: result.assessmentPercentageChangeFormatted,
          barTextColor: TextColors.black,
          color: this.determineBarColor(result.assessmentPercentageChange),
          borderColor: this.determineBorderColor(result.assessmentPercentageChange),
          percent: result.assessmentPercentageChange,
          count: result.assessmentPercentageChange,
        });
      } else {
        row.fullLengthButton = true;
        ariaLabel = 'Assessment not started';
        values.push({
          tooltipLabel: 'not started',
          tooltipValue: null,
          barLabel: 'NOT STARTED',
          color: BarColors.white,
          borderColor: BorderColors.borderForWhite,
          legendId: 'notAssigned',
          borderStyle: BorderStyles.dashed
        });
      }

      row.values = values;
      row.ariaLabel = ariaLabel;

      // TODO: Add tooltip - BLOCKED by API. Count is not included in the response. New card incoming (23679)
      //   if (result.assessmentPercentageChange) {
      //     row.tooltip = `The displayed percentage is based on a total of ${result.count} questions`
      //   }
      chartData.bars.push(row);
    });

    this.zbChart = chartData;

    return chartData;
  }

  determineBarColor(percent: number): BarColors {
    let color: BarColors;
    if (!percent) {
      color = null;
    } else if (percent < 70) {
      color = BarColors.red300;
    } else if (percent < 90) {
      color = BarColors.cream300;
    } else {
      color = BarColors.green300;
    }
    return color;
  }

  determineBorderColor(percent: number): BorderColors {
    let color: BorderColors;
    if (!percent) {
      color = null;
    } else if (percent < 70) {
      color =  BorderColors.borderForRed300;
    } else if (percent < 90) {
      color =  BorderColors.borderForCream300;
    } else {
      color = BorderColors.borderForGreen300;
    }
    return color;
  }

}
